import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody, Container, Spinner } from "reactstrap";
import { File, Upload } from "lucide-react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import _ from "lodash";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import axios from "axios";

import * as api from "services/api";

import { formatCurrencyValue } from "helpers/string";

import LoadingComponent from "components/LoadingComponent";
import { TextInput } from "components/TextInput";
import { SelectInput } from "components/SelectInput";
import { DatePickerInput } from "components/DatePicker";
import { SearchInput } from "components/SearchInput";
import { PRIORITY_OPTIONS } from "types/contractSolicitation";

import {
	FormContainer,
	InputLine,
	Title,
	FullWidthSelectContainer,
	TextAreaInput,
	InputContainer,
	Label,
	FileUploadContainer,
	FileUploadButton,
	HiddenFileInput,
	ButtonsContainer
} from "./styles";

export default function ContractSolicitationEdit() {
	const [contractSolicitation, setContractSolicitation] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [contracts, setContracts] = useState([]);
	const [contractSearchLoading, setContractSearchLoading] = useState(false);
	const [selectedContract, setSelectedContract] = useState(null);
	const [selectedFile, setSelectedFile] = useState(null);
	const [isUpdating, setIsUpdating] = useState(false);

	const formRef = useRef(null);
	let findContractList = null;

	const { token } = useSelector(s => s.login);

	const { id } = useParams();

	async function handleContractSolicitationEdit(event) {
		event.preventDefault();

		try {
			setIsUpdating(true);

			const formData = new FormData(formRef.current);

			let data = {};

			const valueInput = formRef.current.querySelector('input[name="value"]');
			if (valueInput && valueInput.value) {
				const numericValue = valueInput.value.replace(/\D/g, "");
				data.value = numericValue;
			}

			if (selectedContract?.value) {
				data.contractReference = selectedContract.value;
			}

			for (const [key, value] of formData.entries()) {
				if (key === "startDate") {
					data[key] = dayjs(value, "DD/MM/YYYY").toDate();
				} else if (key !== "contractReference" && key !== "value") {
					data[key] = value;
				}
			}

			formData.append("data", JSON.stringify(data));

			if (selectedFile) {
				formData.append("file", selectedFile);
			}

			const response = await axios.put(
				`${api.serverUrl}/contract-solicitation/${id}`,
				formData,
				{
					headers: { Authorization: "Bearer " + token }
				}
			);

			const result = await response.data;

			if (result.isValid) {
				toast.success("Solicitação de contrato atualizada com sucesso.");

				history.go(-1);
			} else {
				toast.error(
					"Houve um erro ao atualizar a solicitação de contrato. Tente novamente mais tarde."
				);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsUpdating(false);
		}
	}

	async function getContractSolicitation() {
		try {
			setIsLoading(true);

			const response = await api.sendGet(`/contract-solicitation/${id}`, {
				Authorization: "Bearer " + token
			});

			const result = await response.json();

			if (result.isValid) {
				setContractSolicitation(result.model.contractSolicitation);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	}

	function handleSearchContracts(templateName) {
		if (!templateName || templateName.trim() === "") {
			setContracts([]);
			setContractSearchLoading(false);
			return;
		}

		if (findContractList) {
			findContractList.cancel();
		}

		setContractSearchLoading(true);

		let filterStr = "";
		if (templateName) filterStr += `&filters[template.name]=${templateName}`;

		findContractList = _.debounce(
			() =>
				api
					.sendGet(
						`/contracts?skip=0&take=10&sort=createdAt&desc=true${filterStr}`,
						{
							Authorization: "Bearer " + token
						}
					)
					.then(response => response.json())
					.then(result => {
						if (result.isValid) {
							const contractOptions = result.model.map(contract => ({
								value: contract._id,
								label: `${contract.template.name} - ID: ${contract._id}`
							}));
							setContracts(contractOptions);
						}
						setContractSearchLoading(false);
					})
					.catch(error => {
						console.error("Erro ao buscar contratos:", error);
						setContractSearchLoading(false);
					}),
			500
		);

		findContractList();
	}

	function handleSelectContract(contract) {
		setSelectedContract(contract);
	}

	function handleTierChange(event) {
		const value = event.target.value;
		setContractSolicitation(prev => ({
			...prev,
			tier: value
		}));
	}

	function handlePriorityChange(event) {
		const value = event.target.value;
		setContractSolicitation(prev => ({
			...prev,
			priority: value
		}));
	}

	function handleChangeFile(file) {
		if (contractSolicitation?.baseContractFile) {
			toast.warning(
				"Documento selecionado. O documento anterior será substituído ao salvar."
			);
		}
		setSelectedFile(file.target.files[0]);
	}

	useEffect(() => {
		getContractSolicitation();
	}, [id]);

	return (
		<Container className="dashboard">
			<Card>
				<CardBody>
					{isLoading ? (
						<LoadingComponent />
					) : (
						<>
							<Title>Alterar dados da solicitação do contrato</Title>

							<FormContainer
								onSubmit={handleContractSolicitationEdit}
								ref={formRef}
							>
								<TextInput
									id="hirer"
									name="hirer"
									placeholder="Buscar contratante"
									label="Contratante"
									disabled={true}
									value={contractSolicitation?.hirer?.businessName}
								/>

								<TextInput
									id="hired"
									name="hired"
									placeholder="Buscar prestador"
									label="Prestador"
									disabled={true}
									value={contractSolicitation?.hired?.businessName}
								/>

								<InputLine>
									<FullWidthSelectContainer>
										<SelectInput
											disabled={true}
											value={contractSolicitation?.contractType}
											name="contractType"
											label="Tipo de contrato"
											options={[
												{ label: "Todos", value: "" },
												{ label: "NDA", value: "nda" },
												{
													label: "Confidencialidade",
													value: "confidentiality"
												},
												{ label: "Comodato", value: "loan" },
												{
													label: "Prestação de Serviço",
													value: "serviceProvision"
												},
												{ label: "Aditivo contratual", value: "addendum" },
												{ label: "Distrato", value: "termination" },
												{ label: "Outros", value: "other" }
											]}
										/>
									</FullWidthSelectContainer>

									<TextInput
										id="role"
										name="role"
										placeholder="Função"
										label="Função"
										defaultValue={contractSolicitation?.role}
									/>
								</InputLine>

								<SearchInput
									id="contractReference"
									name="contractReference"
									placeholder="Buscar um contrato utilizado anteriormente para ser usado de referência"
									label="Contrato de referência (opcional)"
									value={
										selectedContract ||
										(contractSolicitation?.contractReference
											? `${contractSolicitation?.contractReference?.template?.name} - ${contractSolicitation?.contractReference?._id}`
											: "")
									}
									onChange={handleSelectContract}
									onSearch={handleSearchContracts}
									options={contracts}
									loading={contractSearchLoading}
								/>

								<InputLine>
									<FullWidthSelectContainer>
										<SelectInput
											value={contractSolicitation?.tier}
											name="tier"
											label="Tier"
											onChange={handleTierChange}
											options={[
												{ label: "High", value: "high" },
												{ label: "Mid", value: "mid" },
												{ label: "Low", value: "low" }
											]}
										/>
									</FullWidthSelectContainer>

									<FullWidthSelectContainer>
										<SelectInput
											value={contractSolicitation?.priority}
											name="priority"
											label="Prioridade"
											onChange={handlePriorityChange}
											options={PRIORITY_OPTIONS}
										/>
									</FullWidthSelectContainer>
								</InputLine>

								<InputContainer>
									<Label>Atividades realizadas</Label>
									<TextAreaInput
										placeholder="Descreva a(s) atividade(s) realizada(s) pela função. Até 1000 caracteres."
										defaultValue={contractSolicitation?.activitiesDescription}
										name="activitiesDescription"
										id="activitiesDescription"
									/>
								</InputContainer>

								<InputContainer>
									<Label style={{ marginBottom: "8px" }}>Modalidade</Label>
									<div className="radio-options-container">
										<div className="radio-option">
											<input
												type="radio"
												id="hibrido"
												name="modality"
												className="radio-input"
												defaultChecked={
													contractSolicitation?.modality === "hybrid"
												}
												value={"hybrid"}
											/>
											<label htmlFor="hibrido" className="radio-label">
												Híbrido
											</label>
										</div>

										<div className="radio-option">
											<input
												type="radio"
												id="presencial"
												name="modality"
												className="radio-input"
												defaultChecked={
													contractSolicitation?.modality === "onsite"
												}
												value={"onsite"}
											/>
											<label htmlFor="presencial" className="radio-label">
												Presencial
											</label>
										</div>

										<div className="radio-option">
											<input
												type="radio"
												id="homeoffice"
												name="modality"
												className="radio-input"
												defaultChecked={
													contractSolicitation?.modality === "remote"
												}
												value={"remote"}
											/>
											<label htmlFor="homeoffice" className="radio-label">
												Home Office
											</label>
										</div>
									</div>
								</InputContainer>

								<InputLine>
									<TextInput
										id="value"
										name="value"
										placeholder="Ex: R$ 1.000,00"
										label="Remuneração"
										defaultValue={
											contractSolicitation?.value
												? formatCurrencyValue(contractSolicitation.value)
												: ""
										}
										onChange={e => {
											const numericValue = e.target.value.replace(/\D/g, "");
											const formattedValue = formatCurrencyValue(
												Number(numericValue)
											);
											e.target.value = formattedValue;
										}}
									/>

									<TextInput
										id="paymentDate"
										name="paymentDate"
										placeholder="Ex: Todo dia 5 do mês"
										label="Data do pagamento"
										defaultValue={contractSolicitation?.paymentDate}
									/>
								</InputLine>

								<InputLine>
									<TextInput
										id="lifespan"
										name="lifespan"
										placeholder="12"
										type="number"
										label="Vigência (em meses)"
										min={0}
										max={999}
										defaultValue={Number(contractSolicitation?.lifespan)}
									/>
									<DatePickerInput
										id="startDate"
										name="startDate"
										placeholder="Ex: 01/01/1970"
										label="Data de início"
										value={contractSolicitation?.startDate}
									/>
								</InputLine>

								<InputContainer>
									<Label style={{ marginBottom: "8px" }}>
										Possui algum contrato pré-existente? (opcional)
									</Label>
									<FileUploadContainer>
										<FileUploadButton htmlFor="file-upload">
											{selectedFile ? (
												<File size={24} color="#534b99" />
											) : (
												<Upload size={24} color="#534b99" />
											)}
											{selectedFile ? selectedFile.name : "Upload"}
										</FileUploadButton>
										<HiddenFileInput
											id="file-upload"
											type="file"
											accept="*/*"
											onChange={handleChangeFile}
										/>
									</FileUploadContainer>
								</InputContainer>

								<InputContainer>
									<Label>Cláusulas e informações adicionais (opcional)</Label>
									<TextAreaInput
										placeholder="Descreva em tópicos. Até 1000 caracteres."
										defaultValue={contractSolicitation?.additionalInformation}
										name="additionalInformation"
										id="additionalInformation"
									/>
								</InputContainer>

								<ButtonsContainer>
									<button
										type="button"
										onClick={() => history.go(-1)}
										disabled={isUpdating}
									>
										Cancelar
									</button>
									<button type="submit" disabled={isUpdating}>
										{isUpdating ? <Spinner size="sm" /> : "Salvar"}
									</button>
								</ButtonsContainer>
							</FormContainer>
						</>
					)}
				</CardBody>
			</Card>
		</Container>
	);
}
